import type {
  ActiveTab,
  BulkAction,
  Dialog,
  Filters,
  FiltersAsDictWithMultipleValues,
  FiltersWithMultipleValues,
  Pagination,
  SingleAction,
  Sort,
  TabActionDialog,
} from '@common/types'
import { stringify as stringifyQs } from 'qs'
import urlJoin from 'url-join'

export const caseSection = '/cases/'

export const casesAllPath = 'all'

/**
 * Single-valued case-list filters.
 *
 * `createdZone`, `sourceDimension` and `sourceValue` are the acquisition
 * report's drill-through contract — see `acquisitionSourceFilter.ts` for the
 * source pair, and `getFilterVariables` for the zone. They are filters like
 * any other (saved with a tab, counted by `areFiltersApplied`); what makes
 * them special is only that the report builds them rather than an operator.
 */
export enum CasesUrlFiltersEnum {
  ageMin = 'ageMin',
  ageMax = 'ageMax',
  ageUnit = 'ageUnit',
  closedFrom = 'closedFrom',
  closedTo = 'closedTo',
  contactAction = 'contactAction',
  createdFrom = 'createdFrom',
  createdTo = 'createdTo',
  createdZone = 'createdZone',
  phoneNumber = 'phoneNumber',
  emailAddress = 'emailAddress',
  query = 'query',
  sourceDimension = 'sourceDimension',
  sourceValue = 'sourceValue',
}

export enum CasesUrlFiltersWithMultipleValuesEnum {
  assignedToUser = 'assignedToUser',
  customer = 'customer',
  closeReason = 'closeReason',
  outcome = 'outcome',
  type = 'type',
  stage = 'stage',
  status = 'status',
  orderPaymentStatus = 'orderPaymentStatus',
}

export enum CasesUrlFiltersAsDictWithMultipleValuesEnum {
  tag = 'tag',
}

export enum CasesUrlSortField {
  created = 'created',
  assignee = 'assigned',
  customer = 'customer',
}

export type CasesUrlFilters = Filters<CasesUrlFiltersEnum> &
  FiltersWithMultipleValues<CasesUrlFiltersWithMultipleValuesEnum> &
  FiltersAsDictWithMultipleValues<CasesUrlFiltersAsDictWithMultipleValuesEnum>
export type CasesUrlDialog = 'new' | 'assign' | 'reopen' | TabActionDialog

export type CasesUrlSort = Sort<CasesUrlSortField>
export type CasesUrlQueryParams = BulkAction &
  Dialog<CasesUrlDialog> &
  CasesUrlFilters &
  CasesUrlSort &
  Pagination &
  ActiveTab

export const caseListUrl = (browseType: CaseListType, params?: CasesUrlQueryParams): string => {
  const caseList = urlJoin(caseSection, browseType)
  if (params === undefined) {
    return caseList
  } else {
    return urlJoin(caseList, '?' + stringifyQs(params))
  }
}

export const caseListAllUrl = (params?: CasesUrlQueryParams): string =>
  caseListUrl(casesAllPath, params)

export const casesUserAssignedPath = 'assigned'
export const caseListUserAssignedUrl = (params?: CasesUrlQueryParams): string =>
  caseListUrl(casesUserAssignedPath, params)

export const casesTeamAssignedPath = 'team-assigned'
export const caseListTeamAssignedUrl = (params?: CasesUrlQueryParams): string =>
  caseListUrl(casesTeamAssignedPath, params)

export type CaseListType =
  typeof casesAllPath | typeof casesUserAssignedPath | typeof casesTeamAssignedPath

export const caseDetailPath = (browsedType: CaseListType | undefined, id: string) =>
  urlJoin([caseSection, ...(browsedType ? [browsedType] : []), id])

export type CaseUrlDialog =
  | 'cancel'
  | 'email-update'
  | 'phone-update'
  | 'address-update'
  | 'financial-account-add'
  | 'financial-account-update'
  | 'financial-account-delete'
  | 'financial-account-provider-contact-update'
  | 'action-add'
  | 'action-update'
  | 'action-delete'
  // Log the action that finishes a reminder; `id` is the reminder.
  | 'reminder-done'
  | 'close'
  | 'close-done'
  | 'close-reject'
  | 'create-new-for-customer'
  | 'assign'
  | 'assign-self'
  | 'order-initial-create'
  | 'order-addon-create'
  | 'order-update'
  | 'order-cancel'
  | 'order-fee-update'
  | 'order-state-progress'
  | 'order-payment-add'
  | 'order-payment-update'
  | 'order-payment-delete'
  | 'case-document-add'
  | 'case-document-edit'
  | 'case-document-delete'
  | 'customer-document-add'
  | 'customer-document-edit'
  | 'customer-document-delete'
  | 'customer-document-id-card-scan'
  | 'reopen'
  | 'financial-account-document-add'
  | 'financial-account-document-edit'
  | 'financial-account-document-delete'
  | 'order-document-add'
  | 'order-document-edit'
  | 'order-document-delete'
  | 'order-document-generate'
  | 'order-document-request-signature'
  | 'order-document-sync-esign'
  | 'mediation-ticket-create'
  | 'negotiation-ticket-create'
  | 'order-detail'
  | 'customer-phone-add'
  | 'customer-email-add'
  | 'customer-address-add'
  | 'customer-update'
  // Identity check: "Belongs to this customer" / "Different debtor". The move's
  // KTP prefill (NIK, name) stays in CaseDetailContext, never in the URL.
  | 'identity-confirm'
  | 'identity-move'

export type CaseUrlQueryParams = Dialog<CaseUrlDialog> &
  SingleAction &
  Partial<{
    documentType: string
    documentTypeId: string
    tab: string
    historyFilter: string
  }>
export const caseDetailUrl = (
  browseType: CaseListType | undefined,
  id: string,
  params?: CaseUrlQueryParams,
) => caseDetailPath(browseType, encodeURIComponent(id)) + '?' + stringifyQs(params)

export const casesCustomizePath = 'customize'
export const casesCustomizeUrl = (params?: never): string =>
  urlJoin(caseSection, casesCustomizePath) + '?' + stringifyQs(params)

export const casesRemindersPath = 'reminders'

/** The Reminders page tabs; each maps to a `CaseReminderScope`. */
export enum CaseRemindersUrlScope {
  mine = 'mine',
  team = 'team',
  all = 'all',
}

export type CaseRemindersUrlQueryParams = Partial<{
  scope: CaseRemindersUrlScope
  /** The selected Jakarta day, `YYYY-MM-DD`. */
  date: string
}>

export const caseRemindersUrl = (params?: CaseRemindersUrlQueryParams): string => {
  const path = urlJoin(caseSection, casesRemindersPath)
  const query = stringifyQs(params)
  return query ? `${path}?${query}` : path
}
